
import React, { useMemo, useState, useEffect } from 'react';
import { MealSession, Language, DietaryProfile, MealTypeKey } from '../types';
import { t } from '../locales';
import { analyzeDietaryProfile } from '../services/analysisService';

interface StatsViewProps {
  sessions: MealSession[];
  language: Language;
}

type RankingTimeframe = 'total' | 'day' | 'week';
type RankingLimit = 5 | 10 | 20;

const StatsView: React.FC<StatsViewProps> = ({ sessions, language }) => {
  const [dietProfile, setDietProfile] = useState<DietaryProfile | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [rankingTimeframe, setRankingTimeframe] = useState<RankingTimeframe>('total');
  const [rankingLimit, setRankingLimit] = useState<RankingLimit>(5);
  const [isDietDetailsOpen, setIsDietDetailsOpen] = useState(false);

  // --- Metrics Calculation ---

  // 1. Calorie Intake per Day (Last 7 Days)
  const calorieChartData = useMemo(() => {
      const data: { day: string, calories: number }[] = [];
      const today = new Date();
      
      const getSessionCalories = (s: MealSession) => {
          const itemMultiplier = s.items.length;
          if (itemMultiplier === 0) return 0;

          let kcalPerItem = 250; 
          
          const hour = new Date(s.timestamp).getHours();
          const mealType: MealTypeKey = s.manualMealType || (
              hour >= 4 && hour < 11 ? 'breakfast' :
              hour >= 11 && hour < 15 ? 'lunch' :
              hour >= 15 && hour < 18 ? 'afternoonSnack' :
              hour >= 18 || hour < 4 ? 'dinner' : 'snack'
          );

          switch (mealType) {
              case 'breakfast': kcalPerItem = 200; break;
              case 'lunch': kcalPerItem = 450; break;
              case 'dinner': kcalPerItem = 450; break;
              case 'snack': kcalPerItem = 150; break;
              case 'afternoonSnack': kcalPerItem = 150; break;
          }
          
          return itemMultiplier * kcalPerItem;
      };

      for (let i = 6; i >= 0; i--) {
          const d = new Date(today);
          d.setDate(today.getDate() - i);
          const dateStr = d.toDateString();
          
          let totalDayCalories = 0;
          sessions.forEach(s => {
              if (new Date(s.timestamp).toDateString() === dateStr) {
                  totalDayCalories += getSessionCalories(s);
              }
          });
          
          const dayName = d.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'short' });
          // Divide by 3 as requested
          data.push({ day: dayName, calories: Math.round(totalDayCalories / 3) });
      }
      return data;
  }, [sessions, language]);

  // 2. Unique Days Count (Used for ranking calculation)
  const uniqueDaysCount = useMemo(() => {
      return new Set(sessions.map(s => new Date(s.timestamp).toDateString())).size || 1;
  }, [sessions]);

  // 3. Top Ingredients Ranking
  const topIngredients = useMemo(() => {
      const counts: Record<string, number> = {};
      sessions.flatMap(s => s.items).forEach(item => {
          const name = item.name.trim().toLowerCase();
          const display = name.charAt(0).toUpperCase() + name.slice(1);
          counts[display] = (counts[display] || 0) + 1;
      });

      const sorted = Object.entries(counts).sort(([, a], [, b]) => b - a);

      return sorted.map(([name, count]) => {
          let value = count;
          let suffix = '';
          
          if (rankingTimeframe === 'day') {
              value = parseFloat((count / uniqueDaysCount).toFixed(1));
              suffix = '/d';
          } else if (rankingTimeframe === 'week') {
              const weeks = Math.max(1, uniqueDaysCount / 7);
              value = parseFloat((count / weeks).toFixed(1));
              suffix = '/wk';
          }

          return { name, value, rawCount: count, suffix };
      });
  }, [sessions, rankingTimeframe, uniqueDaysCount]);

  // --- AI Diet Analysis ---
  const handleAnalyzeDiet = async (e: React.MouseEvent) => {
      e.stopPropagation(); 
      setIsLoadingProfile(true);
      const allFoodNames = sessions.flatMap(s => s.items.map(i => i.name));
      allFoodNames.reverse();
      
      const result = await analyzeDietaryProfile(allFoodNames, language);
      if (result) {
          setDietProfile(result);
          localStorage.setItem('vital_diet_profile', JSON.stringify(result));
          setIsDietDetailsOpen(true);
      }
      setIsLoadingProfile(false);
  };

  // Load cached profile on mount
  useEffect(() => {
      const cached = localStorage.getItem('vital_diet_profile');
      if (cached) {
          try {
              const parsed = JSON.parse(cached);
              if (parsed.categoryCounts) setDietProfile(parsed);
          } catch (e) {
              localStorage.removeItem('vital_diet_profile');
          }
      }
  }, []);

  // --- Visual Components ---

  const renderMacroBar = (label: string, value: number, colorClass: string) => (
      <div className="flex flex-col gap-1 w-full">
          <div className="flex justify-between text-xs font-medium text-gray-600 dark:text-gray-300">
              <span>{label}</span>
              <span>{value}%</span>
          </div>
          <div className="h-2 w-full bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${colorClass}`} style={{ width: `${value}%` }}></div>
          </div>
      </div>
  );

  const PieChart = ({ data }: { data: Record<string, number> }) => {
      if (!data) return <div className="text-center text-xs text-gray-400 py-4">No category data.</div>;
      const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];
      const total = Object.values(data).reduce((a, b) => a + b, 0);
      if (total === 0) return <div className="text-center text-xs text-gray-400 py-4">No data points.</div>;

      let currentAngle = 0;
      const segments = Object.entries(data).map(([key, value], idx) => {
          const percentage = (value / total) * 100;
          const angle = (value / total) * 360;
          const segment = `${colors[idx % colors.length]} ${currentAngle}deg ${currentAngle + angle}deg`;
          currentAngle += angle;
          return { segment, color: colors[idx % colors.length], key, value, percentage };
      });

      const gradient = `conic-gradient(${segments.map(s => s.segment).join(', ')})`;

      const getLabel = (key: string) => {
          const map: Record<string, string> = { grains: "Grains", proteins: "Proteins", dairy: "Dairy", vegetables: "Vegetables", fruits: "Fruits", fats: "Fats", water: "Water" };
          return map[key] || key;
      };

      return (
          <div className="flex flex-col items-center gap-6 mt-4">
              <div className="w-40 h-40 rounded-full relative shadow-inner" style={{ background: gradient }}>
                  <div className="absolute inset-8 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center shadow-sm">
                      <div className="text-center">
                        <span className="text-xs font-medium text-gray-400 block">Total</span>
                        <span className="text-xl font-bold text-gray-800 dark:text-white">{total}</span>
                      </div>
                  </div>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 w-full">
                  {segments.sort((a,b) => b.value - a.value).map((s, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }}></div>
                              <span className="text-gray-600 dark:text-gray-300 truncate max-w-[100px]">{getLabel(s.key)}</span>
                          </div>
                          <span className="font-bold text-gray-800 dark:text-white">{Math.round(s.percentage)}%</span>
                      </div>
                  ))}
              </div>
          </div>
      );
  };

  return (
    <div className="p-4 pb-32 max-w-md mx-auto flex flex-col gap-6">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">{t(language, 'statsTitle')}</h2>

        {/* 1. Diet Profile Card */}
        <div 
            id="tour-stats-diet"
            onClick={() => dietProfile && setIsDietDetailsOpen(!isDietDetailsOpen)}
            className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden transition-all duration-300 ${dietProfile ? 'cursor-pointer hover:shadow-md' : ''}`}
        >
            <div className="p-5 flex justify-between items-center">
                <div className="flex flex-col">
                    <h3 className="font-bold text-gray-800 dark:text-white flex items-center gap-2 mb-1">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                        {t(language, 'dietType')}
                    </h3>
                    {dietProfile ? (
                        <span className="inline-block self-start px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-xs font-bold uppercase">
                            {dietProfile.dietType}
                        </span>
                    ) : (
                        <span className="text-xs text-gray-400 italic">Tap to analyze</span>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <button 
                        onClick={handleAnalyzeDiet}
                        disabled={isLoadingProfile}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide disabled:opacity-50 transition-colors ${dietProfile ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200' : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100'}`}
                    >
                        {isLoadingProfile ? t(language, 'analyzingDiet') : dietProfile ? t(language, 'update') : t(language, 'analyzeDiet')}
                    </button>
                    {dietProfile && (
                        <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 text-gray-400 transition-transform duration-300 ${isDietDetailsOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                    )}
                </div>
            </div>
            {dietProfile && isDietDetailsOpen && (
                <div className="px-5 pb-5 pt-2 border-t border-gray-100 dark:border-gray-700 animate-slideIn">
                    <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-6">{dietProfile.explanation}</p>
                    <div className="space-y-3 mb-6">
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">{t(language, 'macroSplit')}</h4>
                        {renderMacroBar(t(language, 'protein'), dietProfile.macros.protein, 'bg-blue-500')}
                        {renderMacroBar(t(language, 'digestibleCarbs'), Math.max(0, dietProfile.macros.carbs - (dietProfile.macros.fiber || 0)), 'bg-orange-500')}
                        {renderMacroBar(t(language, 'fiber'), dietProfile.macros.fiber || 0, 'bg-emerald-500')}
                        {renderMacroBar(t(language, 'fat'), dietProfile.macros.fats, 'bg-yellow-500')}
                    </div>
                    <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 text-center">Category Breakdown</h4>
                        <PieChart data={dietProfile.categoryCounts} />
                    </div>
                </div>
            )}
        </div>

        {/* 2. Top Ingredients Ranking */}
        <div id="tour-stats-ranking" className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5">
            <div className="flex flex-col gap-3 mb-4">
                <div className="flex justify-between items-center">
                    <h3 className="font-bold text-gray-800 dark:text-white flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>
                        Top Ingredients
                    </h3>
                </div>
                <div className="flex gap-2">
                    <select value={rankingLimit} onChange={(e) => setRankingLimit(Number(e.target.value) as RankingLimit)} className="flex-1 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-xs font-medium px-2 py-2 text-gray-700 dark:text-gray-200 outline-none">
                        <option value={5}>Top 5 Items</option><option value={10}>Top 10 Items</option><option value={20}>Top 20 Items</option>
                    </select>
                    <select value={rankingTimeframe} onChange={(e) => setRankingTimeframe(e.target.value as RankingTimeframe)} className="flex-1 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-xs font-medium px-2 py-2 text-gray-700 dark:text-gray-200 outline-none">
                        <option value="total">Total Count</option><option value="day">Avg / Day</option><option value="week">Avg / Week</option>
                    </select>
                </div>
            </div>
            <div className="space-y-3">
                {topIngredients.length === 0 ? <div className="text-center text-sm text-gray-400 py-4">No ingredients logged.</div> : topIngredients.slice(0, rankingLimit).map((item, idx) => {
                    const maxVal = topIngredients[0].value;
                    const widthPct = (item.value / maxVal) * 100;
                    return (
                        <div key={idx} className="flex items-center gap-3 animate-slideIn" style={{ animationDelay: `${idx * 0.05}s` }}>
                            <span className="w-4 text-xs font-bold text-gray-400">{idx + 1}</span>
                            <div className="flex-1">
                                <div className="flex justify-between text-xs mb-1">
                                    <span className="font-medium text-gray-800 dark:text-gray-200 truncate">{item.name}</span>
                                    <span className="font-bold text-emerald-600 dark:text-emerald-400">{item.value}{item.suffix}</span>
                                </div>
                                <div className="h-1.5 w-full bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${widthPct}%` }}></div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>

        {/* 3. Calorie Intake (At End) */}
        <div id="tour-stats-volume" className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5">
            <h3 className="font-bold text-gray-800 dark:text-white mb-8 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                {t(language, 'calorieIntake')}
            </h3>
            <div className="flex items-end justify-between h-36 gap-2 mt-6">
                {calorieChartData.map((d, i) => {
                    const max = Math.max(...calorieChartData.map(d => d.calories), 2500); 
                    const heightPct = (d.calories / max) * 100;
                    return (
                        <div key={i} className="flex flex-col items-center flex-1 h-full justify-end group">
                             <div className="w-full relative h-full flex items-end justify-center">
                                 {/* Permanently visible number above bar */}
                                 <div className="absolute -top-7 text-[10px] font-black text-emerald-600 dark:text-emerald-400 whitespace-nowrap text-center w-full">
                                     {d.calories > 0 ? d.calories : ''}
                                 </div>
                                 <div 
                                    className="w-full bg-emerald-200 dark:bg-emerald-900/50 rounded-t-md transition-all duration-500 group-hover:bg-emerald-400" 
                                    style={{ height: `${heightPct}%` }}
                                 ></div>
                             </div>
                             <span className="text-[10px] text-gray-400 mt-2 font-medium">{d.day}</span>
                        </div>
                    );
                })}
            </div>
        </div>

    </div>
  );
};

export default StatsView;
